// TradeSheet — Add trade / Edit trade (Oct 28, 2026).
//
// Robert (via Greg): "I got Alex contacting Jose and cutting deals then it's pushed to me to schedule and I
// have zero idea what has been discussed, planned or any particulars." Whoever agrees a job with a sub
// fills this in the same day: the work, the footage (priced at the sub's list as it's typed; Greg: "Let's
// start with alex putting in the lf"), the total if Jose agreed to something else, what was agreed, and the
// day Jose committed to. One Save does the rest on the server (build-calendar trade_save): the visit goes on
// the Other Trades calendar with Robert, Alex, Jennifer, Jose and the RM invited, and the homeowner gets the
// text Greg approved. The window then shows each step's result, like Schedule build. No in-app notices: the
// invite is the notice.
//
// Edit keeps the day (Move changes it, so the calendar and the homeowner hear about it) and doesn't text.
//
// Oct 29 (Greg): a detach or a reset prices at $2.50 a foot of what comes down or goes back up (gutters,
// downspouts, guards), so those visits ask for that footage instead of a guard brand. Every trade also carries
// the price to the customer, prefilled at the 30% margin; short of it (or at Bardin's cost, or a detach and
// reset sold outside the claim after insurance paid for it) the save says Chad will review it.
//
// Oct 29 (Greg): "will you add soffit/fascia to the optional work. also have jac as the default sub, but make
// available a 'other' sub they would type in." Soffit / fascia is a work of its own (the trim rows on the sub's
// list by the foot, plus off-list lines). The Sub picker starts on JAC Gutters and ends with "Other sub (type the
// name)": a name, and an email and phone if known. That sub has no price list, so its trade is off-list lines
// only (each with its price), and its email is invited in JAC's place.
//
// Oct 29 (Greg): "No need to show JAC Gutters' price. Just show the 30% floor and do math from there", then "no
// need to show the amount bardin pays jac. the reps only care about the amount that we need to charge homeowner
// and where their commission starts." The sheet prices the footage at the sub's list as before (the server does
// the same), but shows only the minimum to charge: per foot (the floor rate, a label), per line and in total (the
// floor on the sub's amounts, tradeText floorOf), the customer's price prefilled at that minimum, and the dollars
// above it (commission, the RM's by default; Chad can share it) or under it (the deal's non-collect bucket, whose
// first $500 is forgiven, if Chad deducts it). No sub figure, no margin %. The "Jose agreed to a different total?"
// box is gone: the cost is the sub's list or the quotes typed on off-list lines (the one sub figure on the sheet,
// needed to work out the minimum); what Jose actually bills is reconciled when his invoice reaches Books. An edit
// of a trade priced on a total agreed with the sub keeps sending that total, so the server never reprices it.
// A homeowner-billed trade's price goes on the install agreement and the invoice (the Scope card picks it up).
//
// Oct 29 (Greg): "if a trade is added after the homeowner has signed, the addendum is made ... we could have it detect
// the agreement is already signed, so a sign and schedule button are below. sign brings up addendum, work and price,
// homeowner signs and is sent a link to open and sign. once signed, the schedule button is available ... all that is
// if we are doing roof not a stand alone retail job." When trade_context says the agreement is signed and the job is
// a roof job, a trade billed to the homeowner is an add-on: the day picker says "Schedule after the homeowner signs
// the add-on" and the save sends no day; the done step shows Sign (texts the homeowner the link) and Schedule (the
// Move flow, once signed). Until signed the trade isn't on the agreement's money or the invoice (trade_sync_deal).

import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  BILLED_TO_LABELS, CUSTOM_ITEM, EMAIL_RE, ITEM_LABELS, PAIR_LIST_LOCKED, TIMING_LABELS, WORK_LABELS, defaultTiming, fetchTradeContext, floorOf, floorRate, fmtMoney2, fmtTradeDay,
  footageOnly, inputsFromLines, itemGroup, linesToEdit, marginCheck, marginShortfall, priceVisits, repMarginReason, roofFitProblem, scheduledTextPlan, settlePrice, shortOfFloorText, tradeCall, tradeEventTitle,
  tradeHoShare, upsideAbove, addendumApplies, addendumStatusAfter, effectiveCustomerLabel, isAddendumOpen, tradeCustomerLabel,
  type BilledTo, type PriceInput, type PriceListItem, type PriceLine, type Trade, type TradeContext, type TradeGuest, type TradePrice, type TradeSaveResult, type TradeTiming, type TradeWork,
} from "../../lib/trades";
import { smsInfo } from "../../../supabase/functions/build-calendar/eventText";
import { CalendarStepText, StepRow, TextStepText, calendarOk, textOk } from "./tradeUi";
import AddendumActions from "./AddendumActions";

export type WorkChoice = TradeWork | "detach_reset" | "detach_install";

const WORK_CHOICES: Array<{ key: WorkChoice; label: string; hint: string }> = [
  { key: "detach_reset", label: "Detach + reset", hint: "Down before the roof, back up after" },
  { key: "detach_install", label: "Detach + new install", hint: "Old down before the roof, new up after" },
  { key: "new_install", label: "New install", hint: "" },
  { key: "service", label: "Service / repair", hint: "" },
  { key: "detach", label: "Detach only", hint: "" },
  { key: "reset", label: "Reset only", hint: "" },
  { key: "soffit_fascia", label: "Soffit / fascia", hint: "Fascia and soffit by the foot" },
  { key: "other", label: "Other", hint: "" },
];

const isPair = (w: WorkChoice) => w === "detach_reset" || w === "detach_install";
/** A detach or a reset asks for what comes down or goes back up, by the foot, whatever the brand. */
const isDR = (w: WorkChoice) => w === "detach" || w === "reset" || w === "detach_reset";
const DR_KEYS = ["gutters", "downspouts", "guards"];
const TRIM_GROUP = "Fascia and soffit";
const GROUPS: Array<{ title: string; keys: string[]; more?: boolean }> = [
  { title: "Gutters", keys: ["gutters", "downspouts"] },
  { title: "Guards", keys: ["roll_lock", "smart_flow", "leaf_free", "leaf_free_high_flow"] },
  { title: TRIM_GROUP, keys: ["fascia_pine", "soffit_pine", "fascia_hardie", "soffit_hardie"], more: true },
  { title: "Siding", keys: ["siding"], more: true },
];
const GUTTER_SIZES = ['5"', '6"', '7"'];
const ARRIVALS = ["", "AM", "PM"];
/** The Sub picker's last choice: a sub typed on the sheet (Greg, Oct 29), with no price list. */
const OTHER_SUB = "other";
const isJac = (name: string) => name.trim().toLowerCase() === "jac gutters";
/** JAC Gutters by default (Greg, Oct 29), else the first sub on the list, else the typed one. */
const defaultVendorId = (vendors: Array<{ id: string; name: string }>): string =>
  (vendors.find((v) => isJac(v.name)) ?? vendors[0])?.id ?? OTHER_SUB;
/** The Sub an edit opens on when its row has no vendors id: the sub its name matches (a legacy row from before
 *  PR #76 names 'JAC Gutters' with no id; review of the Oct 29 branch opened it as an other sub called "JAC
 *  Gutters"), else an other sub typed on the sheet, else (no name at all) the default like a new trade. */
const startVendorId = (vendors: Array<{ id: string; name: string }>, vendorName: string | null | undefined): string => {
  const name = (vendorName ?? "").trim().toLowerCase();
  if (!name) return defaultVendorId(vendors);
  return vendors.find((v) => v.name.trim().toLowerCase() === name)?.id ?? OTHER_SUB;
};
/** What stands in for the price rows when the sub has none to show: an other sub, or soffit / fascia on a list
 *  with no trim rows. */
const NO_LIST_HINT = "No price list for this sub: put each line of the job and its price under Off the list.";

interface CustomLine { label: string; amount: string }
interface VisitState { date: string; noDay: boolean; arrival: string }

interface Props {
  dealId: string;
  /** Edit this visit (its pair partner shares the job's fields). */
  trade?: Trade | null;
  /** The job's price (the pair's, when editing a pair). */
  price?: TradePrice | null;
  /** New: the work to start at (the + Trade on a build starts at Detach + reset). */
  startWork?: WorkChoice;
  onClose: () => void;
  onSaved: (r: TradeSaveResult) => void;
  /** The done step's Schedule (Oct 29): the Move flow for that visit, once the homeowner signed the add-on. */
  onSchedule?: (tradeId: string) => void;
}

type Phase = "form" | "running" | "done";
const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));

export default function TradeSheet({ dealId, trade, price, startWork, onClose, onSaved, onSchedule }: Props) {
  const editing = !!trade;
  const [ctx, setCtx] = useState<TradeContext | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("form");
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<TradeSaveResult | null>(null);

  // ── form ──
  const [work, setWork] = useState<WorkChoice>(trade?.trade_type ?? startWork ?? "new_install");
  const [otherLabel, setOtherLabel] = useState(trade?.details?.other_label ?? "");
  // The sub: a vendors row, or OTHER_SUB with a name (and an email / phone) typed here (Greg, Oct 29). An edited
  // trade with no vendor row is settled once the subs load (startVendorId): the sub its name matches, else an
  // other sub under that name, else (linked from a hand-made event with no sub matched) the default like a new trade.
  const [vendorId, setVendorId] = useState<string>(trade?.vendor_id ?? "");
  const [subName, setSubName] = useState(trade && !trade.vendor_id ? trade.vendor ?? "" : "");
  const [subEmail, setSubEmail] = useState(trade?.details?.vendor_email ?? "");
  const [subPhone, setSubPhone] = useState(trade?.details?.vendor_phone ?? "");
  const [qty, setQty] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const i of trade?.items ?? []) if (i.key !== CUSTOM_ITEM && i.qty !== null) out[i.key] = String(i.qty);
    return out;
  });
  // Detach + new install: what comes down, apart from what goes up (qty).
  const [down, setDown] = useState<Record<string, string>>({});
  // The stored lines this visit's quotes and off-list lines start from. On a detach + new install only its own: the
  // partner keeps its stored lines (visitInputs below, and the server), so seeding the install's off-list lines here
  // too priced them on both visits and the save wrote them onto the detach (review, Oct 30). A detach and its reset
  // share one set of items that this sheet carries, so every line.
  const seedLines = trade ? linesToEdit(price?.lines ?? [], trade.trade_type, !!trade.pair_id) : price?.lines ?? [];
  // A quoted line (interior repair) is stored on the visit as {key, qty: null}, so it's seeded from the visit as
  // well as from the price: Jeremy never gets the price, and a price read can fail or lag (review, Oct 29).
  // Without this an edit silently dropped the quoted line from the job.
  const [quoteOn, setQuoteOn] = useState<Record<string, boolean>>(() => ({
    ...Object.fromEntries((trade?.items ?? []).filter((i) => i.key !== CUSTOM_ITEM && i.qty === null).map((i) => [i.key, true])),
    ...Object.fromEntries(seedLines.filter((l) => l.unit === "quote").map((l) => [l.key, true])),
  }));
  const [quoteAmt, setQuoteAmt] = useState<Record<string, string>>(() =>
    Object.fromEntries(seedLines.filter((l) => l.unit === "quote" && l.amount !== null).map((l) => [l.key, String(l.amount)])));
  const [customs, setCustoms] = useState<CustomLine[]>(() => {
    const fromPrice = seedLines.filter((l) => l.key === CUSTOM_ITEM).map((l) => ({ label: l.label, amount: l.amount === null ? "" : String(l.amount) }));
    if (fromPrice.length) return fromPrice;
    return (trade?.items ?? []).filter((i) => i.key === CUSTOM_ITEM && i.label).map((i) => ({ label: i.label!, amount: "" }));
  });
  const [gutterSize, setGutterSize] = useState(trade?.details?.gutter_size ?? "");
  const [color, setColor] = useState(trade?.details?.color ?? "");
  // A total agreed with the sub that isn't the lines' own, on a trade priced before Oct 29 (the sheet no longer
  // asks for one): an edit sends it back unchanged so the server doesn't reprice the job.
  const [keptAgreed, setKeptAgreed] = useState<number | null>(null);
  const [billedTo, setBilledTo] = useState<BilledTo | "">(trade?.billed_to ?? "");
  // The price to the customer: prefilled at the minimum (the floor) until someone types one.
  const [customer, setCustomer] = useState<string>(price?.customer_total !== null && price?.customer_total !== undefined ? String(price.customer_total) : "");
  const [customerTouched, setCustomerTouched] = useState(price?.customer_total !== null && price?.customer_total !== undefined);
  const [scope, setScope] = useState(trade?.scope_notes ?? "");
  const [visits, setVisits] = useState<VisitState[]>(() =>
    trade
      ? [{ date: trade.scheduled_date ?? "", noDay: !trade.scheduled_date, arrival: trade.arrival_window ?? "" }]
      : [{ date: "", noDay: false, arrival: "" }, { date: "", noDay: false, arrival: "" }]);
  const [timing, setTiming] = useState<TradeTiming | "">(trade?.timing ?? "");
  const [guestOff, setGuestOff] = useState<Set<string>>(new Set());
  const [textHo, setTextHo] = useState<boolean | null>(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchTradeContext(dealId)
      .then((c) => {
        if (!alive) return;
        setCtx(c);
        const start = startVendorId(c.vendors, trade?.vendor);
        setVendorId((v) => v || start);
        if (start !== OTHER_SUB) setSubName("");
        if (!trade && !startWork) setWork(c.deal.roof_start || c.deal.roof_expected ? "detach_reset" : "new_install");
      })
      .catch((e) => { if (alive) setLoadErr((e as Error).message); });
    return () => { alive = false; };
  }, [dealId]); // eslint-disable-line react-hooks/exhaustive-deps

  const vendor = ctx?.vendors.find((v) => v.id === vendorId) ?? null;
  const otherSub = vendorId === OTHER_SUB;
  const subDisplayName = vendor?.name ?? (otherSub && subName.trim() ? subName.trim() : null);
  // Jose is JAC's contact; any other sub goes by its name.
  const contact = vendor ? (isJac(vendor.name) ? "Jose" : vendor.name) : (subDisplayName ?? "the sub");
  // An other sub has no list: the server prices it the same way, from off-list lines alone.
  const list: PriceListItem[] = vendor?.prices ?? [];
  const pricesShown = !!ctx && !ctx.prices_hidden;
  const byKey = useMemo(() => new Map(list.map((p) => [p.key, p])), [list]);
  const roofStart = ctx?.deal.roof_start ?? null;
  const roofForFit = roofStart ?? ctx?.deal.roof_expected ?? null;

  // On an edit, keep a total agreed with the sub when it isn't simply the lines' total (decided once, as the sheet
  // opens: an off-list amount typed later must still count).
  const [agreedLoaded, setAgreedLoaded] = useState(false);

  // ── the items as the server reads them ──
  const workNow: WorkChoice = editing ? trade!.trade_type : work;
  const drMode = isDR(workNow);                       // this visit's footage is what comes down / goes back up
  const trimOnly = workNow === "soffit_fascia";       // fascia and soffit by the foot, plus off-list lines
  const twoBlocks = !editing && work === "detach_install"; // what comes down, then what goes up
  const customLines = (): PriceInput[] => customs.filter((c) => c.label.trim() || c.amount.trim()).map((c) => ({ key: CUSTOM_ITEM, qty: null, label: c.label, amount: num(c.amount) }));
  const ftInputs = (m: Record<string, string>): PriceInput[] => DR_KEYS.flatMap((k) => { const q = num(m[k] ?? ""); return q !== null && q !== 0 ? [{ key: k, qty: q }] : []; });
  const inputs: PriceInput[] = useMemo(() => {
    // A sub typed on the sheet has no list: only the off-list lines go (footage typed under JAC earlier stays out).
    if (otherSub) return customLines();
    if (drMode) return [...ftInputs(qty), ...customLines()];
    const out: PriceInput[] = [];
    for (const p of list) {
      if (itemGroup(p.key) === "work") continue;
      // Soffit / fascia takes the trim rows alone, so footage typed under another work chip isn't sent silently.
      if (trimOnly && itemGroup(p.key) !== "trim") continue;
      if (p.unit === "quote") {
        if (quoteOn[p.key]) out.push({ key: p.key, qty: null, amount: num(quoteAmt[p.key] ?? "") });
        continue;
      }
      const q = num(qty[p.key] ?? "");
      if (q !== null && q !== 0) out.push({ key: p.key, qty: q });
    }
    return [...out, ...customLines()];
  }, [list, qty, quoteOn, quoteAmt, customs, drMode, trimOnly, otherSub]); // eslint-disable-line react-hooks/exhaustive-deps
  const downInputs: PriceInput[] = useMemo(() => (twoBlocks && !otherSub ? ftInputs(down) : []), [down, twoBlocks, otherSub]);
  // The visits priced together, as the server prices them: a pair's two, or this one with its partner. A detach
  // and its reset share the footage (both from this sheet), with an off-list line priced on the detach alone; a
  // detach + new install's partner keeps its stored lines, typed amounts and all (delta review, Oct 29).
  const visitInputs = useMemo((): Array<{ work: TradeWork; items: PriceInput[] }> => {
    if (!editing && work === "detach_reset") return [{ work: "detach", items: inputs }, { work: "reset", items: footageOnly(inputs) }];
    if (!editing && work === "detach_install") return [{ work: "detach", items: downInputs }, { work: "new_install", items: inputs }];
    const w = editing ? trade!.trade_type : (work as TradeWork);
    const own = [{ work: w, items: inputs }];
    if (!editing || !trade!.pair_id) return own;
    const partnerWork = (price?.lines ?? []).find((l) => l.work && l.work !== w)?.work;
    if (!partnerWork) return own;
    if (isDR(w) && isDR(partnerWork)) return [{ work: "detach", items: inputs }, { work: "reset", items: footageOnly(inputs) }];
    return [...own, { work: partnerWork, items: inputsFromLines(price?.lines ?? [], partnerWork) }];
  }, [editing, work, inputs, downInputs, trade, price]);
  const priced = useMemo(() => priceVisits(visitInputs, list), [visitInputs, list]);
  // Other sub picked while editing one visit of a pair priced at the sub's list: the partner's footage would
  // reprice against no list, and this sheet can't show that footage to fix it (review of the Oct 29 branch). One
  // plain line instead of the pricing problems, Save off; the server refuses the same way.
  const pairListLocked = editing && !!trade!.pair_id && otherSub && (price?.lines ?? []).some((l) => l.key !== CUSTOM_ITEM && l.work !== trade!.trade_type);
  const lineItem = (l: PriceLine) => (l.key === CUSTOM_ITEM ? { key: l.key, qty: null, label: l.label } : { key: l.key, qty: l.qty });
  const rateOf = (w: "detach" | "reset") => list.find((p) => p.key === w)?.price ?? null;

  useEffect(() => {
    if (agreedLoaded || !editing || !price || !ctx) return;
    setAgreedLoaded(true);
    if (price.price_status === "agreed" && price.agreed_total !== null && price.agreed_total !== priced.knownTotal) setKeptAgreed(price.agreed_total);
  }, [agreedLoaded, editing, price, ctx, priced.knownTotal]);

  const settled = settlePrice(priced, keptAgreed);

  // ── the minimum to charge (Greg, Oct 29: the 30% floor; short of it Chad reviews) ──
  // An edit uses the target the trade was priced at, as the server does; a new trade the current one.
  const targetPct = (editing ? price?.margin_target_pct : null) ?? ctx?.margin_target_pct ?? 30;
  const floorTotal = floorOf(settled.agreedTotal, targetPct);
  // An insurance-billed detach and reset starts from what the parsed scope pays for it; anything else from the
  // minimum. A detach + new install's price buys new gutters too, which the scope's D&R line never paid for.
  const drOnly = visitInputs.length > 0 && visitInputs.every((v) => isDR(v.work));
  const scopePays = ctx && billedTo === "insurance" && drOnly ? ctx.scope_detach_reset?.rcv_total ?? null : null;
  const suggested = scopePays !== null ? scopePays : settled.agreedTotal !== null && settled.agreedTotal > 0 ? floorTotal : null;
  const customerShown = billedTo === "bardin" ? "" : customerTouched ? customer : suggested !== null ? suggested.toFixed(2) : "";
  const customerTotal = billedTo === "bardin" ? null : num(customerShown);
  // The customer's price against the minimum: + upside (commission on a homeowner-billed trade), - short.
  const upside = upsideAbove(settled.agreedTotal, customerTotal, targetPct);

  // ── the homeowner's add-on (Greg, Oct 29) ──
  // A homeowner-billed price on a roof job whose agreement is signed is an add-on the homeowner signs by text link
  // before the trade gets a day (rule 1 of 20261029g; the server decides the same and refuses a day until signed).
  const addendumNeeded = !!ctx && addendumApplies({
    billedTo: billedTo || null, customerTotal, agreementSigned: !!ctx.deal.agreement_signed, roofJob: !!ctx.deal.roof_job,
  });
  // A new trade: saved with no day. An edit of a visit with a day: a save that makes the job wait on a signature
  // (a new price or new words after signing, the payer becoming the homeowner) is refused, so the sheet says so.
  const addendumLabel = tradeCustomerLabel(visitInputs.map((v) => v.work), priced.lines, { gutter_size: !trimOnly && gutterSize ? gutterSize : undefined, other_label: otherLabel.trim() || undefined });
  const editAddendum = editing && !!ctx && pricesShown && !!price ? (() => {
    // A row stored without a label compares equal on its words, as the server and the trigger do (review, Oct 30).
    const prevLabel = price.customer_label?.trim() ? effectiveCustomerLabel(price.customer_label, price.lines.find((l) => l.work)?.work ?? trade!.trade_type) : null;
    const next = addendumStatusAfter({
      applies: addendumNeeded, customerTotal, label: addendumLabel,
      prev: { status: price.addendum_status ?? "not_needed", customerTotal: price.customer_total, label: prevLabel, payerHomeowner: trade!.billed_to === "homeowner" },
      signed: { amount: price.addendum_amount ?? null, label: price.addendum_label ?? null, at: price.addendum_signed_at ?? null },
    });
    const moved = trade!.billed_to !== (billedTo || null) || price.customer_total !== customerTotal || (prevLabel !== null && prevLabel !== addendumLabel);
    return { next, blocked: isAddendumOpen(next) && (moved || !isAddendumOpen(price.addendum_status)) && !!trade!.scheduled_date };
  })() : null;

  // ── the visits ──
  const pair = !editing && isPair(work);
  const visitWorks: Array<{ work: TradeWork; timing: TradeTiming; label: string }> = pair
    ? [
        { work: "detach", timing: "before_build", label: "Detach day (before the roof)" },
        { work: work === "detach_reset" ? "reset" : "new_install", timing: "after_build", label: work === "detach_reset" ? "Reset day (after the roof)" : "Install day (after the roof)" },
      ]
    : [{
        work: (editing ? trade!.trade_type : work) as TradeWork,
        timing: (timing || (editing ? trade!.timing : defaultTiming(work as TradeWork, !!roofForFit))) as TradeTiming,
        label: `Day ${contact} committed to`,
      }];
  const shownVisits = visits.slice(0, visitWorks.length);
  // A new trade that is an add-on goes in with no day: it is scheduled once the homeowner signs (Oct 29).
  const addendumHoldsDay = !editing && addendumNeeded;
  const dates = shownVisits.map((v) => (addendumHoldsDay || v.noDay || !v.date ? null : v.date));
  const today = ctx?.today ?? "";

  const fit = visitWorks.map((vw, i) => roofFitProblem(vw.timing, dates[i], roofForFit, roofStart ? ctx?.deal.roof_end ?? null : null));
  const pairOrder = pair && dates[0] && dates[1] && dates[1] <= dates[0];
  // An edit keeps the day (Move changes it), so an overdue visit can still have its notes fixed.
  const past = !editing && dates.some((d) => d && today && d < today);

  // ── guests ──
  // A sub typed on the sheet (Greg, Oct 29) gets the standing guests and the RMs (other_sub_guests; from a
  // function deployed before Oct 29, the first sub's list less that sub) plus the typed email once it reads as
  // one. The server keeps that email among the defaults (it reads it back off the row), so unticking it on an
  // edit takes it off the event like any other default.
  const guestPool: TradeGuest[] = useMemo(() => {
    if (vendor) return vendor.guests;
    const base = ctx?.other_sub_guests ?? (ctx?.vendors[0]?.guests ?? []).filter((g) => g.why !== "the sub");
    const email = subEmail.trim().toLowerCase();
    if (!otherSub || !EMAIL_RE.test(email) || base.some((g) => g.email === email)) return base;
    return [...base, { email, name: subName.trim() || "the sub", why: "the sub" }];
  }, [vendor, ctx, otherSub, subEmail, subName]);
  const guests = guestPool.filter((g) => !guestOff.has(g.email)).map((g) => g.email);

  // ── the homeowner text (a new save only) ──
  const canText = !!ctx && ctx.deal.phone_ok && !ctx.deal.opted_out;
  const textOn = !editing && canText && (textHo ?? true);
  // Each visit's text names that visit's own items, as the server texts it (delta review, Oct 29: the detach of a
  // detach + new install previewed with the new gutters coming down).
  const itemsOf = (w: TradeWork) => priced.lines.filter((l) => !l.work || l.work === w).map(lineItem);
  const textPlan = useMemo(() => {
    if (!ctx || editing) return { texts: [] as string[], told: [], skipped: [] as Array<{ detail: string }> };
    const dated = visitWorks.map((vw, i) => ({ work: vw.work, items: itemsOf(vw.work), timing: vw.timing, date: dates[i] })).filter((v): v is typeof v & { date: string } => !!v.date);
    return scheduledTextPlan({ firstName: ctx.deal.first_name, street: ctx.deal.street, roofStart, roofEnd: ctx.deal.roof_end }, dated);
  }, [ctx, editing, JSON.stringify(visitWorks), JSON.stringify(priced.lines), JSON.stringify(dates), roofStart]); // eslint-disable-line react-hooks/exhaustive-deps
  const texts = textPlan.texts;

  const title = ctx ? tradeEventTitle({ homeowner: ctx.deal.homeowner, work: visitWorks[0].work, items: itemsOf(visitWorks[0].work), otherLabel }) : "";
  const check = ctx ? marginCheck({
    cost: settled.agreedTotal, price: customerTotal, billedTo: billedTo || null, targetPct, works: visitInputs.map((v) => v.work), scopeHadIt: ctx.scope_had_detach_reset,
    scopeRcv: ctx.scope_detach_reset?.rcv_total ?? null,
  }) : null;
  // What a price under the minimum costs (Greg, Oct 29: "If they have not used the $500 and this is $300 below
  // 30%, it deducts from there"): Alex sees it before he pitches the homeowner, against the deal's own bucket.
  // Nothing typed yet on a billed job is a missing price, not a shortfall.
  const shortfall = check?.status === "needs_review" && (billedTo === "bardin" || customerTotal !== null)
    ? marginShortfall(settled.agreedTotal, customerTotal, targetPct) : null;
  const bucket = ctx?.deal.bucket ?? null;
  // This trade's own deducted shortfall is in the bucket already; a save that changes the money takes it out.
  const ownLoss = editing && price?.margin_review === "deducted" ? price.margin_loss ?? 0 : 0;
  const underLine = (short: number, whole: boolean): string => {
    const head = whole
      ? `The whole minimum, ${fmtMoney2(short)}, goes to the deal's non-collect bucket; what's over $500 comes off the commission unless Chad allows it.`
      : `${fmtMoney2(short)} under the minimum: it goes to the deal's non-collect bucket; what's over $500 comes off the commission unless Chad allows it.`;
    if (!bucket) return head;
    const before = Math.max(0, Math.round((bucket.non_collect + bucket.trade_margin_loss - ownLoss) * 100) / 100);
    const off = Math.round((Math.max(0, before + short - bucket.allowance) - Math.max(0, before - bucket.allowance)) * 100) / 100;
    const has = before > 0 ? `The bucket has ${fmtMoney2(before)} in it already` : "The bucket is empty";
    return `${head} ${has}, so ${off > 0 ? `if Chad deducts it, ${fmtMoney2(off)} comes off the commission` : "the $500 covers it"}.`;
  };
  // The marginCheck reasons, less the "short of the floor" one the line above says in full.
  const floorText = shortfall !== null && shortfall > 0 ? shortOfFloorText(shortfall, targetPct) : null;
  const otherReasons = (check?.reasons ?? []).flatMap((r) => (floorText && r === floorText ? []
    : floorText && r.endsWith(`; ${floorText}`) ? [`${r.slice(0, -(floorText.length + 2))}.`] : [r]));
  const chadLine = shortfall !== null && shortfall > 0 ? ` ${underLine(shortfall, billedTo === "bardin" || customerTotal === 0)}` : " Chad will review it on the Commission board.";
  // Once the deal's commission is approved or paid, a homeowner-billed trade's price and payer stay put (the server
  // refuses the same way): the trade total and the upside can't reach the stored commission lines (Oct 29).
  const hoBefore = editing && price ? tradeHoShare(trade!.billed_to, price.customer_total, price.agreed_total, price.margin_target_pct ?? targetPct) : { total: 0, upside: 0 };
  const hoAfter = tradeHoShare(billedTo || null, customerTotal, settled.agreedTotal, targetPct);
  const hoMoves = hoBefore.total !== hoAfter.total || hoBefore.upside !== hoAfter.upside;
  const commissionLocked = pricesShown && (!editing || !!price) && !!ctx?.deal.commission_locked && hoMoves;
  // The save put a homeowner-billed price on the deal, or changed one (the done step's agreement line).
  const hoSaved = billedTo === "homeowner" && customerTotal !== null && (!editing || hoBefore.total !== hoAfter.total || trade!.billed_to !== billedTo);

  const problems = [
    !vendorId && "Pick the sub.",
    otherSub && subName.trim().length < 2 && "Type the sub's name.",
    otherSub && !!subEmail.trim() && !EMAIL_RE.test(subEmail.trim()) && "That email doesn't look right.",
    !billedTo && "Say who pays for the work.",
    customerTotal !== null && settled.agreedTotal !== null && customerTotal < 0 && "The customer price doesn't look right.",
    // The deal with the homeowner is usually already made (Greg, Oct 29): the price goes in with the sub's.
    pricesShown && !!billedTo && billedTo !== "bardin" && settled.agreedTotal !== null && customerTotal === null && `Enter the price to the ${billedTo === "insurance" ? "insurance" : "homeowner"}.`,
    scope.trim().length < 3 && "Write down what was agreed.",
    (editing ? trade!.trade_type : work) === "other" && !otherLabel.trim() && "Say what the other work is.",
    pairOrder && "The visit after the roof has to come after the detach.",
    past && "That day has already passed.",
    // The server reads an empty list as "nothing sent" and invites the defaults anyway (review, Oct 29).
    !!guestPool.length && !guests.length && "Tick at least one person to invite.",
    commissionLocked && "This deal's commission is already approved or paid, so a trade billed to the homeowner can't be added or repriced on it. Ask Chad.",
    editAddendum?.blocked && "The homeowner signed the roof agreement, so this new price is an add-on they sign first, and this visit has a day. Call it off with Move (no day yet), then save and tap Sign; give it its day once they sign.",
    ...(pairListLocked ? [PAIR_LIST_LOCKED] : priced.problems),
  ].filter(Boolean) as string[];
  const canSave = phase === "form" && !!ctx && problems.length === 0;

  async function save() {
    setErr(null);
    if (!canSave) return;
    setPhase("running");
    try {
      const r = await tradeCall<TradeSaveResult>({
        action: "trade_save",
        deal_id: dealId,
        ...(editing ? { trade_id: trade!.id } : { work }),
        // A sub typed on the sheet goes by name (vendor_id null); the server keeps its email / phone in details.
        ...(otherSub
          ? { vendor_id: null, vendor_name: subName.trim(), vendor_email: subEmail.trim() || null, vendor_phone: subPhone.trim() || null }
          : { vendor_id: vendorId }),
        items: inputs,
        ...(twoBlocks && !otherSub ? { detach_items: downInputs } : {}),
        // No box for it any more (Oct 29): only a total agreed with the sub on an older trade, sent back as it was.
        agreed_total: keptAgreed,
        customer_total: customerTotal,
        billed_to: billedTo || null,
        details: { gutter_size: !trimOnly && gutterSize ? gutterSize : undefined, color: color.trim() || undefined, other_label: otherLabel.trim() || undefined },
        scope_notes: scope.trim(),
        visits: visitWorks.map((vw, i) => ({
          date: editing ? trade!.scheduled_date : dates[i], arrival: shownVisits[i].arrival || null, timing: vw.timing,
        })),
        guests,
        text_homeowner: textOn,
        // The sheet opened without the job's price and nothing was typed in the price box: keep the stored price
        // (review, Oct 29: a lagging or failed price read must not blank the agreed total).
        ...(editing && !price && !customs.some((c) => c.amount.trim()) && !Object.values(quoteAmt).some((v) => v.trim()) ? { keep_price: true } : {}),
      });
      setResult(r);
      setPhase("done");
      onSaved(r);
    } catch (e) {
      setErr((e as Error).message);
      setPhase("form");
    }
  }

  const close = () => { if (phase !== "running") onClose(); };
  const setVisit = (i: number, patch: Partial<VisitState>) => setVisits((vs) => vs.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  const moreHasValue = GROUPS.filter((g) => g.more).some((g) => g.keys.some((k) => num(qty[k] ?? "")))
    || list.some((p) => p.unit === "quote" && quoteOn[p.key]);
  const otherKeys = list.filter((p) => p.unit !== "quote" && itemGroup(p.key) !== "work" && !GROUPS.some((g) => g.keys.includes(p.key))).map((p) => p.key);
  // A group with none of its rows on this sub's list gets no heading (review of the Oct 29 branch: "Fascia and
  // soffit" stood over nothing on a list with no trim rows); soffit / fascia on such a list is off-list lines only.
  const onList = (g: { keys: string[] }) => g.keys.some((k) => byKey.has(k));
  const trimOffList = trimOnly && !GROUPS.filter((g) => g.title === TRIM_GROUP).some(onList);
  // The rows a detach or reset asks for, at that work's rate (both ways for a detach + reset). QtyRow shows the
  // minimum to charge on them, never the rate itself (Greg, Oct 29).
  const drRows = (both: boolean): PriceListItem[] => {
    const rate = both ? (rateOf("detach") !== null && rateOf("reset") !== null ? rateOf("detach")! + rateOf("reset")! : null)
      : rateOf(workNow === "reset" ? "reset" : "detach");
    return DR_KEYS.map((k) => ({ key: k, label: ITEM_LABELS[k], unit: "ft" as const, price: rate }));
  };
  /** "minimum $3.57 a foot down and $3.57 a foot back up": the floor rate, a label (the line minimums come from
   *  floorOf on the sub's amounts, so they never drift from it). */
  const drNote = (both: boolean) => {
    const d = floorRate(rateOf("detach"), targetPct), r = floorRate(rateOf("reset"), targetPct);
    if (!pricesShown || d === null || r === null) return null;
    return both ? `minimum ${fmtMoney2(d)} a foot down and ${fmtMoney2(r)} a foot back up` : `minimum ${fmtMoney2(workNow === "reset" ? r : d)} a foot`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={close} role="dialog" aria-modal="true" aria-label={editing ? "Edit trade" : "Add a trade"}>
      <div className="bg-white w-full sm:max-w-xl rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-bardin-ink">{phase === "done" ? "Trade saved" : editing ? "Edit trade" : "Add a trade"}</h2>
            <p className="text-sm text-slate-500 mt-0.5 truncate">
              {ctx ? `${ctx.deal.homeowner ?? "(no name)"} · ${ctx.deal.street ?? ""}` : "Loading the job…"}
            </p>
          </div>
          <button onClick={close} aria-label="Close" className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {loadErr && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{loadErr}</div>}
          {!ctx && !loadErr && <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />}

          {ctx && phase !== "done" && (
            <>
              {/* the work */}
              <Field label="The work">
                {editing ? (
                  <div className="text-sm text-slate-700">{WORK_LABELS[trade!.trade_type]}{trade!.pair_id ? " (one of a pair; the job's details below apply to both visits)" : ""}</div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {WORK_CHOICES.map((w) => (
                      <Chip key={w.key} on={work === w.key} onClick={() => { setWork(w.key); setTiming(""); }} title={w.hint || undefined}>{w.label}</Chip>
                    ))}
                  </div>
                )}
                {(editing ? trade!.trade_type : work) === "other" && (
                  <input value={otherLabel} onChange={(e) => setOtherLabel(e.target.value)} maxLength={80} placeholder="What is it? e.g. Touch-up paint"
                         className="mt-2 w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                )}
              </Field>

              {/* the sub: JAC by default, or one typed here (Greg, Oct 29) */}
              <Field label="Sub">
                {ctx.vendors.length === 0 && <p className="text-xs text-rose-700 mb-1">No trade subs are set up yet; type the sub below.</p>}
                <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label="Sub" className="w-full border border-slate-300 rounded px-3 py-2 text-sm">
                  {ctx.vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  <option value={OTHER_SUB}>Other sub (type the name)</option>
                </select>
                {vendor?.primary_email && <p className="text-xs text-slate-400 mt-1">{vendor.primary_email}</p>}
                {otherSub && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input value={subName} onChange={(e) => setSubName(e.target.value)} maxLength={80} placeholder="Sub's name" aria-label="Sub's name"
                           className="sm:col-span-2 w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                    <input type="email" value={subEmail} onChange={(e) => setSubEmail(e.target.value)} maxLength={120} placeholder="Email (optional; gets the invite)" aria-label="Sub's email"
                           className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                    <input type="tel" value={subPhone} onChange={(e) => setSubPhone(e.target.value)} maxLength={40} placeholder="Phone (optional)" aria-label="Sub's phone"
                           className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                    <p className="sm:col-span-2 text-xs text-slate-400">{NO_LIST_HINT}</p>
                  </div>
                )}
              </Field>

              {/* what comes down (a detach + new install), by the foot at the detach rate */}
              {twoBlocks && !otherSub && (
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex justify-between">
                    <span>What comes down</span>
                    {drNote(false) && <span className="normal-case tracking-normal font-normal">detach {drNote(false)}</span>}
                  </div>
                  <div className="px-3 py-2.5 space-y-1.5">
                    {drRows(false).map((it) => (
                      <QtyRow key={it.key} item={it} value={down[it.key] ?? ""} onChange={(v) => setDown((q) => ({ ...q, [it.key]: v }))} showPrice={pricesShown} pct={targetPct} />
                    ))}
                  </div>
                </div>
              )}

              {/* footage, priced at the sub's list (or at the detach / reset rate) */}
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex justify-between">
                  <span>{otherSub ? "The job, line by line" : twoBlocks ? "What goes up" : drMode ? (workNow === "detach_reset" ? "What comes down and goes back up" : workNow === "reset" ? "What goes back up" : "What comes down") : "Footage"}</span>
                  {otherSub
                    ? <span className="normal-case tracking-normal font-normal">no price list: each line with its price</span>
                    : drMode
                      ? (drNote(workNow === "detach_reset") && <span className="normal-case tracking-normal font-normal">{drNote(workNow === "detach_reset")}</span>)
                      : (pricesShown && vendor?.prices_from && <span className="normal-case tracking-normal font-normal">minimum to charge · {listOf(vendor.name)} from {fmtTradeDay(vendor.prices_from)}</span>)}
                </div>
                <div className="px-3 py-2.5 space-y-3">
                  {drMode && !otherSub && (
                    <div className="space-y-1.5">
                      {drRows(workNow === "detach_reset").map((it) => (
                        <QtyRow key={it.key} item={it} value={qty[it.key] ?? ""} onChange={(v) => setQty((q) => ({ ...q, [it.key]: v }))} showPrice={pricesShown} pct={targetPct} />
                      ))}
                    </div>
                  )}
                  {/* soffit / fascia lists the trim rows alone; an other sub has no rows at all, only Off the list */}
                  {!drMode && !otherSub && trimOffList && <p className="text-xs text-slate-400">{NO_LIST_HINT}</p>}
                  {!drMode && !otherSub && GROUPS.filter((g) => onList(g) && (trimOnly ? g.title === TRIM_GROUP : !g.more || showMore || moreHasValue)).map((g) => (
                    <div key={g.title}>
                      <div className="text-[11px] font-bold text-slate-500 mb-1">{g.title}</div>
                      <div className="space-y-1.5">
                        {g.keys.filter((k) => byKey.has(k)).map((k) => (
                          <QtyRow key={k} item={byKey.get(k)!} value={qty[k] ?? ""} onChange={(v) => setQty((q) => ({ ...q, [k]: v }))} showPrice={pricesShown} pct={targetPct} />
                        ))}
                      </div>
                    </div>
                  ))}
                  {!drMode && !otherSub && !trimOnly && otherKeys.map((k) => (
                    <QtyRow key={k} item={byKey.get(k)!} value={qty[k] ?? ""} onChange={(v) => setQty((q) => ({ ...q, [k]: v }))} showPrice={pricesShown} pct={targetPct} />
                  ))}
                  {!drMode && !otherSub && !trimOnly && (showMore || moreHasValue) && list.filter((p) => p.unit === "quote").map((p) => (
                    <div key={p.key} className="text-sm">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={!!quoteOn[p.key]} onChange={(e) => setQuoteOn((q) => ({ ...q, [p.key]: e.target.checked }))} className="accent-emerald-600" />
                        <span>{p.label} <span className="text-[11px] text-slate-400">({vendor?.name ?? "the sub"} quotes it from a picture)</span></span>
                      </label>
                      {quoteOn[p.key] && pricesShown && (
                        <div className="mt-1 ml-6 flex items-center gap-2">
                          <span className="text-xs text-slate-500">Sub's quote</span>
                          <MoneyInput value={quoteAmt[p.key] ?? ""} onChange={(v) => setQuoteAmt((q) => ({ ...q, [p.key]: v }))} placeholder="waiting" label="Sub's quote" />
                          <LineMinimum amount={num(quoteAmt[p.key] ?? "")} pct={targetPct} />
                        </div>
                      )}
                    </div>
                  ))}
                  {!drMode && !otherSub && !trimOnly && !showMore && !moreHasValue && (
                    <button type="button" onClick={() => setShowMore(true)} className="text-xs font-semibold text-bardin-teal underline">
                      More: fascia, soffit, siding, interior repair
                    </button>
                  )}

                  {/* off the list */}
                  <div>
                    <div className="text-[11px] font-bold text-slate-500 mb-1">Off the list</div>
                    {customs.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 mb-1.5">
                        <input value={c.label} onChange={(e) => setCustoms((cs) => cs.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                               maxLength={80} placeholder={otherSub ? "e.g. Replace 40 ft of rotted fascia" : trimOnly ? "e.g. Rake boards" : "e.g. Detach and reset guards"}
                               className="min-w-0 flex-1 border border-slate-300 rounded px-2 py-1.5 text-sm" />
                        {/* the sub's quote for the line: the one sub figure on the sheet, needed for its minimum (Oct 29) */}
                        {pricesShown && <MoneyInput value={c.amount} onChange={(v) => setCustoms((cs) => cs.map((x, j) => (j === i ? { ...x, amount: v } : x)))} placeholder="sub's quote" label="Sub's quote" />}
                        {pricesShown && <LineMinimum amount={num(c.amount)} pct={targetPct} />}
                        <button type="button" aria-label="Remove line" onClick={() => setCustoms((cs) => cs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-slate-600 px-1">×</button>
                      </div>
                    ))}
                    <button type="button" onClick={() => setCustoms((cs) => [...cs, { label: "", amount: "" }])} className="text-xs font-semibold text-bardin-teal underline">
                      + A line that isn't on the list
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {!trimOnly && (
                      <div>
                        <div className="text-[11px] font-bold text-slate-500 mb-1">Gutter size</div>
                        <div className="flex gap-1.5">
                          {GUTTER_SIZES.map((s) => <Chip key={s} on={gutterSize === s} onClick={() => setGutterSize(gutterSize === s ? "" : s)}>{s}</Chip>)}
                        </div>
                      </div>
                    )}
                    <div>
                      <div className="text-[11px] font-bold text-slate-500 mb-1">Color</div>
                      <input value={color} onChange={(e) => setColor(e.target.value)} maxLength={40} placeholder="e.g. Musket Brown" className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
                    </div>
                  </div>
                </div>
              </div>

              {/* who pays (required; Greg, Oct 29: with the price, it decides whether Chad sees it) */}
              <Field label="Who pays for the work">
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(BILLED_TO_LABELS) as BilledTo[]).map((k) => (
                    <Chip key={k} on={billedTo === k} onClick={() => setBilledTo(k)}>{BILLED_TO_LABELS[k]}</Chip>
                  ))}
                </div>
                {/* the price block below says the same when it's shown; this is for someone kept off prices */}
                {!pricesShown && ctx.scope_had_detach_reset && (visitWorks[0].work === "detach" || visitWorks[0].work === "reset") && billedTo && billedTo !== "insurance" && (
                  <p className="text-xs text-amber-700 mt-1.5">
                    ⚠ The insurance scope paid for the detach and reset; the deal marks it not performing ("{ctx.scope_not_performing}"). Billing it to the {billedTo === "bardin" ? "company" : "homeowner"} sends it to Chad.
                  </p>
                )}
              </Field>

              {/* the price, in the rep's words (Greg, Oct 29): the minimum to charge, where the commission starts, and
                  the customer's price against it. No sub figure and no margin %; Chad's list keeps the sub's price. */}
              {pricesShown && (
                <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm space-y-2">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-slate-600">
                      {billedTo === "homeowner" ? "Minimum to charge the homeowner" : billedTo === "insurance" ? "Minimum to bill the insurance" : billedTo === "bardin" ? "Minimum (Bardin pays the sub)" : "Minimum to charge"}
                      {billedTo === "homeowner" && <span className="block text-[11px] text-slate-400">where the commission starts</span>}
                    </span>
                    <span className="font-semibold tabular-nums">{floorTotal !== null ? fmtMoney2(floorTotal) : "—"}</span>
                  </div>
                  <div className={`text-xs ${settled.status === "quote_pending" ? "text-amber-700" : "text-slate-500"}`}>
                    {settled.status === "quote_pending"
                      ? "Waiting on the sub's quote; the minimum shows once it's in. It stays in Needs attention until then. "
                      : keptAgreed !== null ? "The minimum uses the total agreed with the sub. " : ""}
                    Prices stay in the CRM; they never go on the calendar.
                  </div>
                  {billedTo !== "bardin" && (
                    <>
                      <label className="flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                        <span className="text-slate-700 font-medium">Price to the {billedTo === "insurance" ? "insurance" : "homeowner"}</span>
                        <MoneyInput value={customerShown} onChange={(v) => { setCustomer(v); setCustomerTouched(true); }} label={`Price to the ${billedTo === "insurance" ? "insurance" : "homeowner"}`} />
                      </label>
                      {check && (
                        <div className={`text-xs ${check.status === "ok" ? "text-emerald-700" : check.status === "needs_review" ? "text-amber-700" : "text-slate-500"}`}>
                          {check.status === "ok" && check.atScope && <>✓ Billed at what the scope pays ({ctx.scope_detach_reset.lines.map((l) => `${l.description} ${l.rcv !== null ? fmtMoney2(l.rcv) : ""}`).join(", ")}), so Chad doesn't need to see it.</>}
                          {check.status === "ok" && !check.atScope && (
                            billedTo === "homeowner" && upside !== null && upside > 0
                              ? <>✓ {fmtMoney2(upside)} above the minimum: commission (the RM by default; Chad can share it).</>
                              : billedTo === "homeowner" ? <>✓ At the minimum: the commission starts above it.</>
                                : <>✓ At or above the minimum.</>
                          )}
                          {check.status === "needs_review" && <>⚠ {otherReasons.join(" ")}{chadLine}{scopePays !== null && !customerTouched ? ` The scope pays ${fmtMoney2(scopePays)} for it.` : ""}</>}
                          {check.status === "unknown" && <>{!billedTo ? "Say who pays for the work." : settled.status === "quote_pending" ? "The minimum shows once the sub's quote is in." : "Put in the footage to see the minimum."}</>}
                        </div>
                      )}
                    </>
                  )}
                  {billedTo === "bardin" && check && check.status === "needs_review" && (
                    <div className="text-xs text-amber-700">⚠ {otherReasons.join(" ")}{chadLine}</div>
                  )}
                </div>
              )}

              {/* what was agreed */}
              <Field label="What we agreed">
                <textarea value={scope} onChange={(e) => setScope(e.target.value)} rows={4} maxLength={4000}
                          placeholder={`Everything you and ${contact} agreed: what comes down, what goes up, color, access, anything about the homeowner. This goes on the calendar event.`}
                          className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
              </Field>

              {/* when */}
              <div className="space-y-3">
                <div className="text-xs text-slate-500">
                  {roofStart
                    ? <>Roof: <b className="text-slate-700">{fmtTradeDay(roofStart)}{ctx.deal.roof_end ? ` - ${fmtTradeDay(ctx.deal.roof_end)}` : ""}</b> (booked)</>
                    : ctx.deal.roof_expected
                      ? <>Roof: not booked yet, expected around <b className="text-slate-700">{fmtTradeDay(ctx.deal.roof_expected)}</b></>
                      : ctx.deal.built ? <>The roof was built {fmtTradeDay(ctx.deal.built)}.</> : <>No roof on the books for this job.</>}
                </div>
                {visitWorks.map((vw, i) => (
                  <div key={i}>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{vw.label}</label>
                    {editing ? (
                      <div className="text-sm text-slate-700">
                        {trade!.scheduled_date ? fmtTradeDay(trade!.scheduled_date) : "No day yet"}
                        <span className="text-xs text-slate-400"> · change the day with Move, so the calendar and the homeowner hear about it</span>
                      </div>
                    ) : addendumHoldsDay ? (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                        <b>Schedule after the homeowner signs the add-on</b>
                        <span className="block text-xs mt-0.5">They signed the roof agreement, so this trade billed to them is an add-on. Save it, tap Sign to text them the link, and Schedule once they've signed.</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <input type="date" value={shownVisits[i].noDay ? "" : shownVisits[i].date} min={today} disabled={shownVisits[i].noDay}
                               onChange={(e) => setVisit(i, { date: e.target.value })} className="border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50" aria-label={vw.label} />
                        <label className="flex items-center gap-1.5 text-xs text-slate-600">
                          <input type="checkbox" checked={shownVisits[i].noDay} onChange={(e) => setVisit(i, { noDay: e.target.checked })} className="accent-slate-600" />
                          No day yet
                        </label>
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="text-[11px] text-slate-500">Arrival:</span>
                      {ARRIVALS.map((a) => <Chip key={a || "all"} on={(shownVisits[i].arrival || "") === a} onClick={() => setVisit(i, { arrival: a })}>{a || "All day"}</Chip>)}
                    </div>
                    {fit[i] && <p className="text-xs text-amber-700 mt-1">⚠ {fit[i]}{!roofStart ? " (against the expected date)" : ""} You can still save it.</p>}
                    {!editing && !pair && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="text-[11px] text-slate-500">Against the roof:</span>
                        {(Object.keys(TIMING_LABELS) as TradeTiming[]).map((t) => (
                          <Chip key={t} on={vw.timing === t} onClick={() => setTiming(t)}>{TIMING_LABELS[t]}</Chip>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* guests */}
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Invited to the Other Trades event
                </div>
                <div className="px-3 py-2 space-y-1">
                  {guestPool.map((g) => (
                    <label key={g.email} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!guestOff.has(g.email)} className="accent-emerald-600"
                             onChange={(e) => setGuestOff((s) => { const n = new Set(s); if (e.target.checked) n.delete(g.email); else n.add(g.email); return n; })} />
                      <span>{g.name} <span className="text-[11px] text-slate-400">· {g.why}</span></span>
                    </label>
                  ))}
                  {otherSub && !guestPool.some((g) => g.why === "the sub") && (
                    <p className="text-xs text-slate-400">Type the sub's email above to invite them.</p>
                  )}
                  {title && <p className="text-[11px] text-slate-400 pt-1">Title: <span className="text-slate-600">{title}</span></p>}
                  {!ctx.calendar_configured && <p className="text-xs text-amber-700">The Other Trades calendar isn't connected, so the trade saves without a Google event.</p>}
                </div>
              </div>

              {/* the homeowner */}
              {!editing && (
                <div className="space-y-2 text-sm">
                  <label className="flex items-start gap-2">
                    <input type="checkbox" checked={textOn} disabled={!canText} onChange={(e) => setTextHo(e.target.checked)} className="accent-emerald-600 mt-1" />
                    <span>
                      Text the homeowner
                      {ctx.deal.opted_out ? <span className="block text-xs text-slate-400">They replied STOP, so no texts.</span>
                        : !ctx.deal.phone_ok ? <span className="block text-xs text-slate-400">No phone on file.</span>
                          : <span className="block text-xs text-slate-400">After 9 PM it waits until 8 AM. A later move of a day or less sends nothing.</span>}
                    </span>
                  </label>
                  {textOn && (texts.length ? texts.map((t, i) => {
                    const info = smsInfo(t);
                    return (
                      <div key={i} className="rounded-xl bg-slate-100 px-3 py-2.5 text-[13px] text-slate-800 leading-snug">
                        {t}
                        <div className="text-[10px] text-slate-400 mt-1.5">{info.chars} characters · {info.segments} text{info.segments === 1 ? "" : "s"}</div>
                      </div>
                    );
                  }) : <p className="text-xs text-slate-400">{textPlan.skipped.length ? textPlan.skipped[0].detail : "No text until the trade has a day."}</p>)}
                  {textOn && texts.length > 0 && textPlan.skipped.length > 0 && (
                    <p className="text-xs text-amber-700">{textPlan.skipped.map((x) => x.detail).join(" ")}</p>
                  )}
                </div>
              )}

              {problems.length > 0 && phase === "form" && (
                <ul className="text-xs text-slate-500 list-disc pl-5">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
              )}
              {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
            </>
          )}

          {phase === "done" && result && (
            <div className="space-y-2.5 text-sm">
              <StepRow ok label="Trade saved">
                {result.trades.map((t) => `${WORK_LABELS[t.work]} ${t.date ? fmtTradeDay(t.date) : "(no day yet)"}`).join(", then ")}
              </StepRow>
              {result.price_error && (
                <StepRow ok={false} warn label="Price">The trade is saved but its price didn't save: {result.price_error}. Open it and save again.</StepRow>
              )}
              {result.price && (
                <StepRow ok={result.price.status !== "quote_pending" && result.price.margin_review !== "needs_review"} label="Price">
                  <DonePrice p={result.price} billedTo={billedTo || null} />
                </StepRow>
              )}
              {/* A homeowner-billed price goes on the install agreement and the invoice (Greg, Oct 29: "the homeowner
                  agreement picks it up from there"). After the agreement is signed, on a roof job, it is an add-on the
                  homeowner signs by text link (Sign below); until then it's off the invoice and gets no day. */}
              {result.price && !result.price.kept && hoSaved && (
                <StepRow ok={!result.addendum || !isAddendumOpen(result.addendum.status)} label={result.addendum && result.addendum.status !== "not_needed" ? "Add-on to the roof agreement" : "Install agreement"}>
                  {result.addendum && isAddendumOpen(result.addendum.status)
                    ? "The homeowner already signed the roof agreement, so this goes on an add-on they sign by text link: tap Sign below. Once they sign it's on the invoice, due at completion, and Schedule gives it its day."
                    : result.addendum?.status === "signed"
                      ? "The homeowner already signed the add-on for this price. It's on the invoice, due at completion."
                      : ctx?.deal.agreement_signed
                        ? (ctx.deal.roof_job === false
                          ? "It goes on the invoice as its own line, due at completion. This job has no roof, so its own contract covers it: no add-on."
                          : "It goes on the invoice as its own line, due at completion.")
                        : "It goes on the install agreement and the invoice as its own line, due at completion. If the agreement was already sent, the Scope card asks for it to be resubmitted for Chad's OK."}
                </StepRow>
              )}
              {result.addendum && result.addendum.status !== "not_needed" && (
                <AddendumActions
                  variant="done"
                  tradeId={result.addendum.trade_id}
                  status={result.addendum.status}
                  sentAt={result.addendum.sent_at}
                  signedAt={result.addendum.signed_at}
                  canAct
                  schedule={result.trades.filter((t) => !t.date).map((t) => ({ id: t.id, label: VISIT_WORDS[t.work] ?? "the visit" }))}
                  onSchedule={onSchedule}
                />
              )}
              {result.calendar.length === 0 && (
                <StepRow ok={false} label="Other Trades calendar">
                  {result.addendum && isAddendumOpen(result.addendum.status)
                    ? <>No day yet, so nothing on the calendar: it gets its day once the homeowner signs the add-on (Schedule above).</>
                    : <>No day yet, so nothing on the calendar. Move it when {contact} gives a day.</>}
                </StepRow>
              )}
              {result.calendar.map((c, i) => (
                <StepRow key={i} ok={calendarOk(c)} warn={c.status === "error"} label={result.calendar.length > 1 ? `Other Trades calendar (visit ${i + 1})` : "Other Trades calendar"}>
                  <CalendarStepText c={c} />
                </StepRow>
              ))}
              {!editing && (
                <StepRow ok={textOk(result.homeowner_text) || result.homeowner_text.status === "skipped"} warn={result.homeowner_text.status === "failed"} label="Homeowner text">
                  <TextStepText r={result.homeowner_text} />
                </StepRow>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 rounded-b-xl">
          {phase === "done" ? (
            <button onClick={onClose} className="px-4 py-1.5 rounded bg-slate-800 text-white text-sm font-medium hover:bg-slate-900">Done</button>
          ) : (
            <>
              <button onClick={close} disabled={phase === "running"} className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800 disabled:opacity-50">Cancel</button>
              <button onClick={save} disabled={!canSave} className="px-4 py-1.5 rounded bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {phase === "running" ? "Saving…" : editing ? "Save changes" : "Save trade"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Schedule the detach" / "Schedule the reset" on the done step of a pair. */
const VISIT_WORDS: Partial<Record<TradeWork, string>> = { detach: "the detach", reset: "the reset", new_install: "the install" };

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="block text-sm font-medium text-slate-700 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Chip({ on, onClick, children, title }: { on: boolean; onClick: () => void; children: ReactNode; title?: string }) {
  return (
    <button type="button" onClick={onClick} title={title}
            className={"px-2.5 py-1 rounded-full border text-xs font-medium " + (on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400")}>
      {children}
    </button>
  );
}

/** "JAC Gutters' list", "Smith Gutter Co's list" */
function listOf(name: string | null | undefined): string {
  if (!name) return "the sub's list";
  return `${name}${/s$/i.test(name) ? "'" : "'s"} list`;
}

/** A footage row. `item.price` is the sub's rate (the server prices at it too), but the row shows only the minimum
 *  to charge (Greg, Oct 29): the floor rate as a label, and the line's minimum from floorOf on the sub's amount
 *  (210 ft of gutters: $1,875.00, not 210 × $8.93 = $1,875.30). */
function QtyRow({ item, value, onChange, showPrice, pct }: { item: PriceListItem; value: string; onChange: (v: string) => void; showPrice: boolean; pct: number }) {
  const q = value.trim() === "" ? 0 : Number(value);
  const minimum = showPrice && item.price !== null && Number.isFinite(q) && q > 0 ? floorOf(Math.round(q * item.price * 100) / 100, pct) : null;
  const rate = showPrice ? floorRate(item.price, pct) : null;
  const unit = item.unit === "sq" ? "squares" : "ft";
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="min-w-0 flex-1 leading-tight">{item.label || ITEM_LABELS[item.key] || item.key}</span>
      <input type="number" inputMode="decimal" min={0} step="any" value={value} onChange={(e) => onChange(e.target.value)}
             aria-label={`${item.label} ${unit}`} className="w-20 border border-slate-300 rounded px-2 py-1 text-sm text-right tabular-nums" />
      <span className="w-24 text-[11px] text-slate-500 leading-tight">{unit}{rate !== null ? <span className="block">min {fmtMoney2(rate)}/{item.unit === "sq" ? "sq" : "ft"}</span> : null}</span>
      {showPrice && <span className="w-20 text-right text-xs tabular-nums text-slate-600" title="Minimum to charge for this line">{minimum !== null ? fmtMoney2(minimum) : ""}</span>}
    </div>
  );
}

/** The minimum to charge on a quote the sub gave for one line, in grey beside it. */
function LineMinimum({ amount, pct }: { amount: number | null; pct: number }) {
  const m = amount !== null && Number.isFinite(amount) && amount > 0 ? floorOf(amount, pct) : null;
  return <span className="shrink-0 text-[11px] text-slate-400 tabular-nums">{m !== null ? `min ${fmtMoney2(m)}` : ""}</span>;
}

/** The done step's price, in the sheet's words: the minimum to charge, the customer's price against it, Chad's
 *  answer. The server's floor_total / upside when it sends them, else the same helpers on its numbers. */
function DonePrice({ p, billedTo }: { p: NonNullable<TradeSaveResult["price"]>; billedTo: BilledTo | null }) {
  const pct = p.margin_target_pct ?? 30;
  const floor = p.floor_total !== undefined ? p.floor_total : floorOf(p.agreed_total, pct);
  const up = p.upside !== undefined ? p.upside : upsideAbove(p.agreed_total, p.customer_total, pct);
  const payer = billedTo === "insurance" ? "Insurance" : billedTo === "homeowner" ? "Homeowner" : "Customer";
  const against = up === null ? "" : up > 0 ? `, ${fmtMoney2(up)} above the minimum${billedTo === "homeowner" ? ": commission (the RM by default; Chad can share it)" : ""}`
    : up < 0 ? `, ${fmtMoney2(-up)} under the minimum` : ", at the minimum";
  return (
    <>
      {p.kept ? (p.kept_reason === "visit_done" ? "Kept as it was (the job's first visit is done). " : "Kept as it was (the sheet opened without it). ") : ""}
      {p.status === "quote_pending" || floor === null ? "Waiting on a price from the sub. " : `Minimum ${fmtMoney2(floor)}${billedTo === "bardin" ? "; Bardin pays the sub" : ""}. `}
      {p.customer_total !== null && billedTo !== "bardin" ? `${payer} ${fmtMoney2(p.customer_total)}${against}. ` : ""}
      {p.margin_review === "needs_review" ? `Chad settles it on the Commission board: ${repMarginReason(p.margin_review_reason, floor, p.customer_total, pct) ?? ""} If he deducts the shortfall, it joins the deal's non-collect bucket (the first $500 is forgiven).`
        : p.margin_review === "allowed" ? "Allowed by Chad, no recourse."
          : p.margin_review === "deducted" ? "Chad sent the shortfall to the deal's non-collect bucket." : ""}
    </>
  );
}

function MoneyInput({ value, onChange, placeholder, label }: { value: string; onChange: (v: string) => void; placeholder?: string; label?: string }) {
  return (
    <span className="inline-flex items-center border border-slate-300 rounded px-2 py-1 bg-white">
      <span className="text-slate-400 text-sm">$</span>
      <input type="number" inputMode="decimal" min={0} step="0.01" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={label}
             className="w-24 text-sm text-right tabular-nums outline-none" />
    </span>
  );
}
